/// <amd-module name="Wit/WorkItemGitLinks/GitLinkUtils" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLocationService } from "VSS/Platform/Location";
import { ILinkIdentifier, ILinkedArtifactAdditionalData, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { LinkTypeFieldIds, RegisteredLinkTypeNames, ToolIds } from "Wit/WorkItemLinksRegistration/Constants";
import { getLinkTypeDisplayName } from "Wit/WorkItemLinksRegistration/LinkUtils";
import { GitCommitRef, GitPullRequest, GitRef, GitUserDate, PullRequestStatus } from "VC/Common/Contracts/Git";
import * as Resources from "Wit/WorkItemGitLinks/Resources";
import { GitRepository } from "Repos/Common/Contracts/Git";

// Example: vstfs:///Git/Commit/456bb6ae-82de-4bf0-aae2-66ec2d830e61%2Fb105a9f1-37f8-4c6f-8ded-5edb510a98d4%2Fbabb1422d200e99ec3db74141eed56a38303716f
const commitUriPattern = /^vstfs:\/\/\/Git\/Commit\/(?<teamProjectId>[a-zA-Z0-9\-]+)%2F(?<repositoryId>[a-zA-Z0-9\-]+)%2F(?<commitSHA>[a-zA-Z0-9]+)$/i;
export interface CommitInfo {
    linkIdentifier: ILinkIdentifier;
    teamProjectId: string;
    repositoryId: string;
    commitSHA: string;
}
interface CommitCaptureGroups {
    groups: CommitInfo;
}

// Example: vstfs:///Git/Ref/456bb6ae-82de-4bf0-aae2-66ec2d830e61%2Fb105a9f1-37f8-4c6f-8ded-5edb510a98d4%2FGBmy-branch
// Note: I am just using .+ to match the branch name - there are rules about what constitutes a
// valid ref name (https://git-scm.com/docs/git-check-ref-format), but we will just trust that
// the creator of the link didn't do anything crazy.
const branchUriPattern = /^vstfs:\/\/\/Git\/Ref\/(?<teamProjectId>[a-zA-Z0-9\-]+)%2F(?<repositoryId>[a-zA-Z0-9\-]+)%2FGB(?<branchName>.+)$/i;
export interface BranchInfo {
    linkIdentifier: ILinkIdentifier;
    teamProjectId: string;
    repositoryId: string;
    branchName: string;
}
interface BranchCaptureGroups {
    groups: BranchInfo;
}

// Example: vstfs:///Git/Ref/456bb6ae-82de-4bf0-aae2-66ec2d830e61%2Fb105a9f1-37f8-4c6f-8ded-5edb510a98d4%2FGTmain-tag
// Same note for tag names as for branch names above.
const tagUriPattern = /^vstfs:\/\/\/Git\/Ref\/(?<teamProjectId>[a-zA-Z0-9\-]+)%2F(?<repositoryId>[a-zA-Z0-9\-]+)%2FGT(?<tagName>.+)$/i;
export interface TagInfo {
    linkIdentifier: ILinkIdentifier;
    teamProjectId: string;
    repositoryId: string;
    tagName: string;
}
interface TagCaptureGroups {
    groups: TagInfo;
}

// Example: vstfs:///Git/PullRequestId/456bb6ae-82de-4bf0-aae2-66ec2d830e61%2Fb105a9f1-37f8-4c6f-8ded-5edb510a98d4%2F1
const pullRequestUriPattern = /^vstfs:\/\/\/Git\/PullRequestId\/(?<teamProjectId>[a-zA-Z0-9\-]+)%2F(?<repositoryId>[a-zA-Z0-9\-]+)%2F(?<rawPullRequestId>\d+)$/i;
interface RawPullRequestInfo {
    linkIdentifier: ILinkIdentifier;
    teamProjectId: string;
    repositoryId: string;
    rawPullRequestId: string;
}
export interface PullRequestInfo extends RawPullRequestInfo {
    pullRequestId: number;
}
interface PullRequestCaptureGroups {
    groups: RawPullRequestInfo;
}

export type GitLinkInfo =
    | (CommitInfo & { linkType: typeof RegisteredLinkTypeNames.Commit })
    | (TagInfo & { linkType: typeof RegisteredLinkTypeNames.Tag })
    | (BranchInfo & { linkType: typeof RegisteredLinkTypeNames.Branch })
    | (PullRequestInfo & { linkType: typeof RegisteredLinkTypeNames.PullRequest; pullRequestId: number });

export function extractGitLinkInfo(linkIdentifier: ILinkIdentifier): GitLinkInfo | undefined {
    const uri = linkIdentifier.FilePath ?? "";
    const linkType = linkIdentifier.OriginalName ?? "";

    // Note: the type assertions on `regex.exec` are needed (and safe) because tsc doesn't know what
    //       capture groups will be present at compile time, but we can be certain what they will be.
    switch (linkType) {
        case RegisteredLinkTypeNames.Commit:
            const commitMatch = commitUriPattern.exec(uri) as (RegExpExecArray & CommitCaptureGroups) | null;
            if (commitMatch === null) {
                console.error(`Invalid commit uri ${uri}`);
                return undefined;
            }
            return { ...commitMatch.groups, linkType, linkIdentifier };

        case RegisteredLinkTypeNames.Branch:
            const branchMatch = branchUriPattern.exec(uri) as (RegExpExecArray & BranchCaptureGroups) | null;
            if (branchMatch === null) {
                console.error(`Invalid ref uri ${uri}`);
                return undefined;
            }
            return { ...branchMatch.groups, linkType, linkIdentifier };

        case RegisteredLinkTypeNames.Tag:
            const tagMatch = tagUriPattern.exec(uri) as (RegExpExecArray & TagCaptureGroups) | null;
            if (tagMatch === null) {
                console.error(`Invalid ref uri ${uri}`);
                return undefined;
            }
            return { ...tagMatch.groups, linkType, linkIdentifier };

        case RegisteredLinkTypeNames.PullRequest:
            const prMatch = pullRequestUriPattern.exec(uri) as (RegExpExecArray & PullRequestCaptureGroups) | null;
            if (prMatch === null) {
                console.error(`Invalid pull request uri ${uri}`);
                return undefined;
            }
            const pullRequestId = parseInt(prMatch.groups.rawPullRequestId);
            return { ...prMatch.groups, linkType, pullRequestId, linkIdentifier };

        default:
            console.error(`Unsupported link type ${linkType}`);
            return undefined;
    }
}

export function constructPullRequestUri(pr: GitPullRequest, repo: GitRepository): string {
    return `vstfs:///Git/PullRequestId/${repo.project.id}%2F${repo.id}%2F${pr.pullRequestId}`;
}

export function constructBranchUri(branch: GitRef, repo: GitRepository): string {
    const branchName = formatBranchName(branch.name);
    return `vstfs:///Git/Ref/${repo.project.id}%2F${repo.id}%2FGB${encodeURIComponent(branchName)}`;
}

export function constructCommitUri(commit: GitCommitRef, repo: GitRepository): string {
    return `vstfs:///Git/Commit/${repo.project.id}%2F${repo.id}%2F${commit.commitId}`;
}

export function constructTagUri(tag: GitRef, repo: GitRepository): string {
    const prefix = "refs/tags/";
    const tagName = tag.name.startsWith(prefix) ? tag.name.substr(prefix.length) : tag.name;
    return `vstfs:///Git/Ref/${repo.project.id}%2F${repo.id}%2FGT${encodeURIComponent(tagName)}`;
}

/**
 * Returns the destination url of the link 
 */
export function constructLinkUrl(pageContext: IVssPageContext, linkIdentifier: ILinkIdentifier): string {
    const info = extractGitLinkInfo(linkIdentifier);
    if (!info) {
        return "#";
    }

    const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");

    try {
        switch (info.linkType) {
            case RegisteredLinkTypeNames.Branch:
                return locationService.routeUrl("ms.vss-code-web.files-route-git", {
                    project: info.teamProjectId,
                    "vc.GitRepositoryName": info.repositoryId,
                    version: "GB" + formatBranchName(decodeURIComponent(info.branchName))
                });
            case RegisteredLinkTypeNames.Commit:
                return locationService.routeUrl("ms.vss-code-web.commit-route", {
                    project: info.teamProjectId,
                    "vc.GitRepositoryName": info.repositoryId,
                    parameters: info.commitSHA
                });
            case RegisteredLinkTypeNames.PullRequest:
                return locationService.routeUrl("ms.vss-code-web.pull-request-review-route", {
                    project: info.teamProjectId,
                    "vc.GitRepositoryName": info.repositoryId,
                    parameters: info.pullRequestId.toString()
                });
            case RegisteredLinkTypeNames.Tag:
                return locationService.routeUrl("ms.vss-code-web.files-route-git", {
                    project: info.teamProjectId,
                    "vc.GitRepositoryName": info.repositoryId,
                    version: "GT" + formatTagName(decodeURIComponent(info.tagName))
                });
            default:
                return "#";
        }
    } catch (e: unknown) {
        return "#";
    }
}

export function commitToDisplayData(pageContext: IVssPageContext, commit: GitCommitRef, linkIdentifier: ILinkIdentifier): IResolvedLinkDisplayData;
export function commitToDisplayData(pageContext: IVssPageContext, commit: GitCommitRef, repo: GitRepository): IResolvedLinkDisplayData;
export function commitToDisplayData(
    pageContext: IVssPageContext,
    commit: GitCommitRef,
    linkIdentifierOrRepo: ILinkIdentifier | GitRepository
): IResolvedLinkDisplayData {
    const linkIdentifier = isRepository(linkIdentifierOrRepo)
        ? {
              tool: ToolIds.Git,
              FldID: LinkTypeFieldIds.ExternalLink,
              OriginalName: RegisteredLinkTypeNames.Commit,
              FilePath: constructCommitUri(commit, linkIdentifierOrRepo)
          }
        : linkIdentifierOrRepo;

    return {
        linkIdentifier,
        tool: ToolIds.Git,
        icons: [{ iconName: "BranchCommit" }, authorToBasicIdentityRef(commit.author)],
        id: commit.commitId,
        displayId: commit.commitId.substr(0, 7),
        title: commit.comment,
        state: undefined,
        linkTypeDisplayName: getLinkTypeDisplayName(RegisteredLinkTypeNames.Commit),
        registeredLinkType: RegisteredLinkTypeNames.Commit,
        updateDate: commit.committer.date,
        uri: constructLinkUrl(pageContext, linkIdentifier),
        additionalData: {
            commitRef: commit
        }
    };
}

export function branchToDisplayData(
    pageContext: IVssPageContext,
    branch: GitRef,
    linkIdentifier: ILinkIdentifier,
    commitAdditionalData: ILinkedArtifactAdditionalData | undefined,
    prAdditionalData: ILinkedArtifactAdditionalData | undefined
): IResolvedLinkDisplayData;
export function branchToDisplayData(
    pageContext: IVssPageContext,
    branch: GitRef,
    repo: GitRepository,
    commitAdditionalData?: ILinkedArtifactAdditionalData,
    prAdditionalData?: ILinkedArtifactAdditionalData
): IResolvedLinkDisplayData;
export function branchToDisplayData(
    pageContext: IVssPageContext,
    branch: GitRef,
    linkIdentifierOrRepo: ILinkIdentifier | GitRepository,
    commitAdditionalData: ILinkedArtifactAdditionalData | undefined,
    prAdditionalData: ILinkedArtifactAdditionalData | undefined
): IResolvedLinkDisplayData {
    const linkIdentifier = isRepository(linkIdentifierOrRepo)
        ? {
              tool: ToolIds.Git,
              FldID: LinkTypeFieldIds.ExternalLink,
              OriginalName: RegisteredLinkTypeNames.Branch,
              FilePath: constructBranchUri(branch, linkIdentifierOrRepo)
          }
        : linkIdentifierOrRepo;

    return {
        linkIdentifier,
        tool: ToolIds.Git,
        icons: [{ iconName: "OpenSource" }, branch.creator],
        id: branch.name,
        displayId: "",
        title: formatBranchName(branch.name),
        state: undefined,
        linkTypeDisplayName: getLinkTypeDisplayName(RegisteredLinkTypeNames.Branch),
        registeredLinkType: RegisteredLinkTypeNames.Branch,
        // updateDate: branch.statuses,
        uri: constructLinkUrl(pageContext, linkIdentifier),
        additionalData: {
            branchRef: branch,
            commit: commitAdditionalData,
            pr: prAdditionalData
        }
    };
}

/**
 * @param tagName A tag name or ref name
 * @returns The tag name
 * 
 * @example
 * ```
 * formatTagName("refs/tags/my-tag"); // "my-tag"
 * formatTagName("tags/my-tag");      // "my-tag"
 * formatTagName("my-tag");           // "my-tag"
 * ```
 */
export function formatTagName(tagName: string) {
    if (tagName.startsWith("refs/tags/")) {
        return tagName.substr("refs/tags/".length);
    }

    if (tagName.startsWith("tags/")) {
        return tagName.substr("tags/".length);
    }

    return tagName;
}

/**
 * @param tagName A tag name or ref name
 * @returns The tag ref name
 * 
 * @example
 * ```
 * formatTagName("refs/tags/my-tag"); // "refs/tags/my-tag"
 * formatTagName("tags/my-tag");      // "refs/tags/my-tag"
 * formatTagName("my-tag");           // "refs/tags/my-tag"
 * ```
 */
export function formatTagRefName(tagName: string) {
    if (tagName.startsWith("refs/tags/")) {
        return tagName;
    }

    if (tagName.startsWith("tags/")) {
        return "refs/" + tagName;
    }

    return "refs/tags/" + tagName;
}

/**
 * @param tagName A branch name or ref name
 * @returns The branch name
 * 
 * @example
 * ```
 * formatTagName("refs/heads/my-branch"); // "my-branch"
 * formatTagName("heads/my-branch");      // "my-branch"
 * formatTagName("my-branch");            // "my-branch"
 * ```
 */
export function formatBranchName(branchName: string) {
    if (branchName.startsWith("refs/heads/")) {
        return branchName.substring("refs/heads/".length);
    }

    if (branchName.startsWith("heads/")) {
        return branchName.substring("heads/".length);
    }

    return branchName;
}

/**
 * @param tagName A branch name or ref name
 * @returns The branch ref name
 * 
 * @example
 * ```
 * formatTagName("refs/heads/my-branch"); // "refs/heads/my-branch"
 * formatTagName("heads/my-branch");      // "refs/heads/my-branch"
 * formatTagName("my-branch");            // "refs/heads/my-branch"
 * ```
 */
export function formatBranchRefName(branchName: string) {
    if (branchName.startsWith("refs/heads/")) {
        return branchName;
    }

    if (branchName.startsWith("heads/")) {
        return "refs/" + branchName;
    }

    return "refs/heads/" + branchName;
}

export function tagToDisplayData(pageContext: IVssPageContext, tag: GitRef, linkIdentifier: ILinkIdentifier): IResolvedLinkDisplayData;
export function tagToDisplayData(pageContext: IVssPageContext, tag: GitRef, repo: GitRepository): IResolvedLinkDisplayData;
export function tagToDisplayData(
    pageContext: IVssPageContext,
    tag: GitRef,
    linkIdentifierOrRepo: ILinkIdentifier | GitRepository
): IResolvedLinkDisplayData {
    const linkIdentifier = isRepository(linkIdentifierOrRepo)
        ? {
              tool: ToolIds.Git,
              FldID: LinkTypeFieldIds.ExternalLink,
              OriginalName: RegisteredLinkTypeNames.Tag,
              FilePath: constructTagUri(tag, linkIdentifierOrRepo)
          }
        : linkIdentifierOrRepo;

    return {
        linkIdentifier,
        tool: ToolIds.Git,
        icons: [{ iconName: "Tag" }, tag.creator],
        id: tag.name,
        displayId: "",
        title: formatTagName(tag.name),
        state: undefined,
        linkTypeDisplayName: getLinkTypeDisplayName(RegisteredLinkTypeNames.Tag),
        registeredLinkType: RegisteredLinkTypeNames.Tag,
        updateDate: undefined, // TODO
        uri: constructLinkUrl(pageContext, linkIdentifier),
        additionalData: {
            tagRef: tag
        }
    };
}

export function pullRequestToDisplayData(
    pageContext: IVssPageContext,
    pullRequest: GitPullRequest,
    linkIdentifier: ILinkIdentifier
): IResolvedLinkDisplayData;
export function pullRequestToDisplayData(pageContext: IVssPageContext, pullRequest: GitPullRequest, repo: GitRepository): IResolvedLinkDisplayData;
export function pullRequestToDisplayData(
    pageContext: IVssPageContext,
    pullRequest: GitPullRequest,
    linkIdentifierOrRepo: ILinkIdentifier | GitRepository
): IResolvedLinkDisplayData {
    const linkIdentifier = isRepository(linkIdentifierOrRepo)
        ? {
              tool: ToolIds.Git,
              FldID: LinkTypeFieldIds.ExternalLink,
              OriginalName: RegisteredLinkTypeNames.PullRequest,
              FilePath: constructPullRequestUri(pullRequest, linkIdentifierOrRepo)
          }
        : linkIdentifierOrRepo;

    return {
        linkIdentifier,
        tool: ToolIds.Git,
        icons: [{ iconName: "BranchPullRequest" }, pullRequest.createdBy],
        id: pullRequest.pullRequestId,
        displayId: "" + pullRequest.pullRequestId,
        title: pullRequest.title,
        state: getFriendlyPRStatus(pullRequest.status),
        linkTypeDisplayName: getLinkTypeDisplayName(RegisteredLinkTypeNames.PullRequest),
        registeredLinkType: RegisteredLinkTypeNames.PullRequest,
        updateDate: pullRequest.creationDate, // TODO: use different dates based on the status
        uri: constructLinkUrl(pageContext, linkIdentifier),
        additionalData: {
            pullRequest: pullRequest
        }
    };
}

export function authorToBasicIdentityRef(commitAuthor: GitUserDate) {
    return {
        displayName: commitAuthor.name,
        id: commitAuthor.email,
        email: commitAuthor.email
    };
}

function getFriendlyPRStatus(status: PullRequestStatus): string {
    switch (status) {
        case PullRequestStatus.NotSet:
            return Resources.PullRequestStatusNotSet;
        case PullRequestStatus.Active:
            return Resources.PullRequestStatusActive;
        case PullRequestStatus.Abandoned:
            return Resources.PullRequestStatusAbandoned;
        case PullRequestStatus.Completed:
            return Resources.PullRequestStatusCompleted;
        case PullRequestStatus.All:
        // This is never actually the status on a PR - it is used for searching
        default:
            return "";
    }
}

function isRepository(value: GitRepository | ILinkIdentifier): value is GitRepository {
    // This check doesn't allow tsc to infer the type on its own because ILinkIdentifier
    // has an index property, so it _could_ have a "project" property.  Technically this
    // isn't fully typesafe, but that seems like an unlikely category of bug to me.
    return "project" in value;
}
